import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { GetOrganizationAffiliation } from "./GetOrganizationAffiliation";
import { OrganizationAffiliation, Organization } from "fhir/r5";
import { isTeleconsultoriaAffiliation } from "src-next/modules/teleconsultoria/affiliation/helpers/isTeleconsultoriaAffiliation";

// Tipos para os parâmetros e retorno do hook
interface FetchOrganizationAffiliationParams {
  primaryOrganization?: string;
  count?: number;
  includePrimaryOrg?: boolean;
  includeParticipatingOrg?: boolean;
}

interface Bundle {
  entry?: Array<{ resource: OrganizationAffiliation | Organization }>;
}

interface UseOrganizationAffiliationReturn {
  organizationAffiliation: OrganizationAffiliation[];
  primaryOrganizations: Organization[];
  participatingOrganizations: Organization[];
  loading: boolean;
  fetchOrganizationAffiliation: (
    params: FetchOrganizationAffiliationParams
  ) => Promise<{
    affiliations: OrganizationAffiliation[];
    primaryOrgs: Organization[];
    participatingOrgs: Organization[];
  }>;
  clearOrganizationAffiliation: () => void;
}

// Hook para buscar e gerenciar planos de saúde
export const useOrganizationAffiliation =
  (): UseOrganizationAffiliationReturn => {
    const [organizationAffiliation, setOrganizationAffiliation] = useState<
      OrganizationAffiliation[]
    >([]);
    const [primaryOrganizations, setPrimaryOrganizations] = useState<
      Organization[]
    >([]);
    const [participatingOrganizations, setParticipatingOrganizations] =
      useState<Organization[]>([]);
    const [loading, setLoading] = useState(false);

    // Função para buscar planos de saúde
    const fetchOrganizationAffiliation = useCallback(
      async ({
        primaryOrganization,
        count,
        includePrimaryOrg,
        includeParticipatingOrg,
      }: FetchOrganizationAffiliationParams) => {
        setLoading(true);
        try {
          // Buscar planos de saúde com os parâmetros fornecidos
          const response = (await GetOrganizationAffiliation({
            primaryOrganization,
            count,
            includePrimaryOrg,
            includeParticipatingOrg,
          })) as Bundle;

          const affiliations: OrganizationAffiliation[] = [];
          const primaryOrgs: Organization[] = [];
          const participatingOrgs: Organization[] = [];

          if (response?.entry?.length) {
            // Separar recursos por tipo
            response.entry.forEach((entry) => {
              const resource = entry.resource;
              if (!resource) return;

              switch (resource.resourceType) {
                case "OrganizationAffiliation":
                  // Afiliação de teleconsultoria (code HIE/HIO) usa o mesmo recurso,
                  // mas não é convênio: fica fora de todas as telas de planos/pagamento.
                  if (
                    isTeleconsultoriaAffiliation(
                      resource as OrganizationAffiliation,
                    )
                  ) {
                    break;
                  }
                  affiliations.push(resource as OrganizationAffiliation);
                  break;
                case "Organization":
                  // Verificar se é primary ou participating baseado no type
                  const orgType = (resource as Organization).type?.[0]
                    ?.coding?.[0]?.code;
                  if (orgType === "prov") {
                    primaryOrgs.push(resource as Organization);
                  } else if (orgType === "pay") {
                    participatingOrgs.push(resource as Organization);
                  }
                  break;
              }
            });
          }

          setOrganizationAffiliation(affiliations);
          setPrimaryOrganizations(primaryOrgs);
          setParticipatingOrganizations(participatingOrgs);

          return {
            affiliations,
            primaryOrgs,
            participatingOrgs,
          };
        } catch (error) {
          toast.error("Erro ao carregar planos de saúde");
          setOrganizationAffiliation([]);
          setPrimaryOrganizations([]);
          setParticipatingOrganizations([]);
          throw error;
        } finally {
          setLoading(false);
        }
      },
      []
    );

    // Função para limpar o estado dos planos
    const clearOrganizationAffiliation = useCallback(() => {
      setOrganizationAffiliation([]);
      setPrimaryOrganizations([]);
      setParticipatingOrganizations([]);
    }, []);

    return {
      organizationAffiliation,
      primaryOrganizations,
      participatingOrganizations,
      loading,
      fetchOrganizationAffiliation,
      clearOrganizationAffiliation,
    };
  };
